import BaseAlert from '@/components/base/BaseAlert';
import BaseButton from '@/components/base/BaseButton';
import BaseDrawer from '@/components/base/BaseDrawer';
import BaseLoading from '@/components/base/BaseLoading';
import { useIntl } from '@/i18n';
import { loadAdminPermission } from '@/services/iam-admin';
import type { AdminAccountDetail, AdminPermissionDetail } from '@/types';
import { localFailureKey, serverMessageOf } from '@/utils/http';
import { CloseOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import AdminPermissionContent from './AdminPermissionContent';

interface AdminPermissionDrawerProps {
  detail: AdminAccountDetail;
  onClose: () => void;
}

/** 权限地图打开才请求，关闭取消，加载不阻塞静态外壳。 */
export default function AdminPermissionDrawer({
  detail,
  onClose,
}: AdminPermissionDrawerProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const t = (key: string) =>
    intl.formatMessage({ id: 'adminAcct.detail.' + key });
  const [value, setValue] = useState<AdminPermissionDetail>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    setValue(undefined);
    setError(undefined);
    setLoading(true);
    void loadAdminPermission(detail.adminId, { signal: controller.signal })
      .then(
        (response) => {
          if (!controller.signal.aborted) setValue(response);
        },
        (reason: unknown) => {
          if (!controller.signal.aborted)
            setError(
              serverMessageOf(reason) ??
                intl.formatMessage({ id: localFailureKey(reason) }),
            );
        },
      )
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [detail.adminId]);

  return (
    <BaseDrawer
      open
      title={
        t('permissionTitle') + ' · ' + (detail.accountName ?? detail.loginEmail)
      }
      onClose={onClose}
      className="[&>.ant-drawer-body]:flex [&>.ant-drawer-body]:flex-col"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <BaseButton icon={<CloseOutlined aria-hidden />} onClick={onClose}>
            {intl.formatMessage({ id: 'common.close' })}
          </BaseButton>
          {value?.boundArchiveNo && (
            <BaseButton
              type="primary"
              icon={<SafetyCertificateOutlined aria-hidden />}
              onClick={() => {
                onClose();
                navigate('/sys/grant');
              }}
            >
              {t('grantOverview')}
            </BaseButton>
          )}
        </div>
      }
    >
      {loading ? (
        <div className="relative flex-1 min-h-60">
          <BaseLoading
            overlay
            label={intl.formatMessage({ id: 'common.loading' })}
          />
        </div>
      ) : error ? (
        <BaseAlert type="error" title={error} />
      ) : value ? (
        <AdminPermissionContent detail={detail} value={value} />
      ) : null}
    </BaseDrawer>
  );
}
